import React from 'react';
import Input from '../../../components/ui/Input';
import { Button } from '../../../components/ui/Button';
import Spinner from '../../../components/ui/Spinner';
import { AppIcon } from '../../../components/icons/AppIcon';

export interface LoginFormProps {
  email: string;
  password: string;
  isLoading: boolean;
  error?: string | null;
  inviteToken?: string | null;
  onEmailChange: (v: string) => void;
  onPasswordChange: (v: string) => void;
  onSubmit: (e: React.FormEvent) => void;
  onNavigateToRegister: () => void;
}

const LoginForm: React.FC<LoginFormProps> = ({
  email, password, isLoading, error, inviteToken,
  onEmailChange, onPasswordChange, onSubmit, onNavigateToRegister,
}) => (
  <div className="flex flex-col gap-s8">
    <header className="flex flex-col items-center gap-s2 text-center">
      <h1 id="login-title" className="text-2xl text-default md:text-3xl">
        {inviteToken ? 'Aceite o convite' : 'Bem-vindo de volta'}
      </h1>
      <p className="text-sm text-secondary">
        {inviteToken
          ? 'Faça login para continuar e aceitar o convite da organização.'
          : 'Insira suas credenciais para acessar sua conta Adlitics.'}
      </p>
    </header>

    {inviteToken && (
      <aside
        className="flex items-start gap-s3 rounded-lg border border-brand-200 bg-primary-subtle px-s4 py-s3 text-left"
        aria-label="Convite pendente"
      >
        <AppIcon name="info" className="mt-0.5 size-4 shrink-0 text-primary" />
        <div>
          <p className="text-label text-primary">Convite pendente</p>
          <p className="mt-s1 text-sm text-secondary">
            Após o login, você confirmará o convite na próxima etapa.
          </p>
        </div>
      </aside>
    )}

    {error && (
      <div
        role="alert"
        className="flex items-start gap-s3 rounded-lg border border-error/30 bg-danger-bg px-s4 py-s3 text-left"
      >
        <AppIcon name="alert" className="mt-0.5 size-4 shrink-0 text-danger-text" />
        <p className="text-sm font-medium text-danger-text">{error}</p>
      </div>
    )}

    <form onSubmit={onSubmit} className="flex flex-col gap-s5 text-left" aria-busy={isLoading}>
      <Input
        label="E-mail"
        id="email"
        type="email"
        placeholder="voce@empresa.com"
        value={email}
        onChange={(e) => onEmailChange(e.target.value)}
        required
        autoComplete="email"
        inputSize="lg"
        autoFocus
      />

      <div>
        <div className="mb-s2 flex items-center justify-between gap-s2">
          <label htmlFor="password" className="input-label">
            Senha
          </label>
          <button
            type="button"
            className="text-sm font-medium text-primary transition-colors hover:text-primary-dark"
          >
            Esqueceu a senha?
          </button>
        </div>
        <Input
          id="password"
          type="password"
          placeholder="••••••••"
          value={password}
          onChange={(e) => onPasswordChange(e.target.value)}
          required
          autoComplete="current-password"
          inputSize="lg"
        />
      </div>

      <Button
        type="submit"
        variant="default"
        size="lg"
        disabled={isLoading}
        className="mt-s1 min-h-11 w-full touch-manipulation hover:shadow-brand"
      >
        {isLoading ? <Spinner size="sm" className="mr-s2" /> : null}
        {isLoading ? 'Entrando...' : 'Entrar na conta'}
      </Button>
    </form>

    <p className="text-center text-sm text-secondary">
      Não tem uma conta?{' '}
      <button
        type="button"
        onClick={onNavigateToRegister}
        className="font-medium text-primary transition-colors hover:text-primary-dark"
      >
        Cadastre-se
      </button>
    </p>
  </div>
);

export default LoginForm;
